import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Package, Calendar, Search } from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { ProductCard } from "@/components/product-card";
import { ProductQuickViewModal } from "@/components/product-quick-view-modal";
import { useShortList } from "@/contexts/short-list-context";
import { cn } from "@/lib/utils";

interface Specification {
  label: string;
  value: string;
}

interface ActionCard {
  id: string;
  packId: string;
  title: string;
  description: string | null;
  category: string | null;
  image: string | null;
  specs: string | Specification[] | null;
  contactEmail: string | null;
  contactName: string | null;
  datasheetUrl?: string | null;
  externalUrl?: string | null;
  articleId: string | null;
  companyId: string | null;
  isActive: boolean;
  sortOrder: number;
  shortlistButtonText: string | null;
  productType: string | null;
  technologyType: string | null;
}

function parseSpecs(specs: string | Specification[] | null): Specification[] {
  if (!specs) return [];
  if (Array.isArray(specs)) return specs;
  try {
    return JSON.parse(specs);
  } catch {
    return [];
  }
}

interface ActionCardPack {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  status: string;
  tags: string[] | null;
  coverImage: string | null;
  publishedAt: string | null;
  cards: ActionCard[];
}

const categoryFilters = [
  { value: "all", label: "All Products" },
  { value: "valves", label: "Valves" },
  { value: "hydraulics", label: "Hydraulics" },
  { value: "electronics", label: "Electronics" },
  { value: "robotics", label: "Robotics" },
  { value: "sensors", label: "Sensors" },
  { value: "motors", label: "Motors" },
  { value: "controllers", label: "Controllers" },
  { value: "actuators", label: "Actuators" },
  { value: "power", label: "Power" },
  { value: "connectors", label: "Connectors" },
  { value: "networking", label: "Networking" },
  { value: "services", label: "Services" },
];

export default function ActionCardsDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { items, addItem, removeItem } = useShortList();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [quickViewCard, setQuickViewCard] = useState<ActionCard | null>(null);

  const { data: pack, isLoading, error } = useQuery<ActionCardPack>({
    queryKey: [`/api/public/action-card-packs/${slug}`],
    enabled: !!slug,
  });

  const isCardInEnquiry = (cardId: string) => {
    return items.some(item => item.actionCardId === cardId);
  };

  const handleAddToEnquiry = (card: ActionCard) => {
    if (isCardInEnquiry(card.id)) {
      const item = items.find(i => i.actionCardId === card.id);
      if (item) removeItem(item.id);
    } else {
      addItem({
        type: "action_card",
        actionCardId: card.id,
        title: card.title,
        category: card.category || undefined,
        image: card.image || undefined,
      });
    }
  };

  const availableCategories = useMemo(() => {
    if (!pack?.cards) return [];
    const categories = new Set(
      pack.cards
        .filter(c => c.isActive && c.category)
        .map(c => c.category!.toLowerCase())
    );
    return categoryFilters.filter(
      f => f.value === "all" || categories.has(f.value)
    );
  }, [pack?.cards]);

  const activeCards = useMemo(() => {
    if (!pack?.cards) return [];
    return pack.cards.filter(card => card.isActive);
  }, [pack?.cards]);

  const isCardMatch = (card: ActionCard) => {
    if (selectedCategory !== "all") {
      const cardCategory = card.category?.toLowerCase() || "";
      if (cardCategory !== selectedCategory) return false;
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesTitle = card.title.toLowerCase().includes(query);
      const matchesDescription = card.description?.toLowerCase().includes(query);
      if (!matchesTitle && !matchesDescription) return false;
    }
    
    return true;
  };

  const filteredCards = useMemo(() => {
    return activeCards.filter(isCardMatch);
  }, [activeCards, selectedCategory, searchQuery]);

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayedCards, setDisplayedCards] = useState<ActionCard[]>([]);
  const prevFilterRef = useRef({ category: selectedCategory, query: searchQuery });

  useEffect(() => {
    if (displayedCards.length === 0 && filteredCards.length > 0) {
      setDisplayedCards(filteredCards);
      return;
    }
    
    if (prevFilterRef.current.category !== selectedCategory || 
        prevFilterRef.current.query !== searchQuery) {
      setIsTransitioning(true);
      
      const timer = setTimeout(() => {
        setDisplayedCards(filteredCards);
        setIsTransitioning(false);
      }, 200);
      
      prevFilterRef.current = { category: selectedCategory, query: searchQuery };
      return () => clearTimeout(timer);
    }
  }, [filteredCards, selectedCategory, searchQuery]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <SiteHeader />
        <div className="max-w-7xl mx-auto px-4 pt-4 w-full">
          <PageBreadcrumb items={[{ label: "Products", href: "/products" }, { label: "Loading…" }]} />
        </div>
        <main className="flex-1 bg-background">
          <div className="container mx-auto px-4 py-8">
            <Skeleton className="h-8 w-64 mb-4" />
            <Skeleton className="h-4 w-96 mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-lg border p-4">
                  <Skeleton className="h-48 w-full mb-4" />
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full mb-4" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (error || !pack) {
    return (
      <div className="min-h-screen flex flex-col">
        <SiteHeader />
        <main className="flex-1 bg-background">
          <div className="container mx-auto px-4 py-16 text-center">
            <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
            <h1 className="text-2xl font-bold mb-2">Collection Not Found</h1>
            <p className="text-muted-foreground mb-6">
              This product collection doesn't exist or is no longer available.
            </p>
            <Button asChild>
              <Link href="/products">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Collections
              </Link>
            </Button>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      <div className="container mx-auto px-4 pt-4">
        <PageBreadcrumb items={[{ label: "Products", href: "/products" }, { label: pack.name }]} />
      </div>
      <main className="flex-1 bg-background">
        <div className="bg-muted/30 border-b">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold mb-2">{pack.name}</h1>
                {pack.description && (
                  <p className="text-muted-foreground max-w-2xl mb-4">
                    {pack.description}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  {pack.publishedAt && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      Published {new Date(pack.publishedAt).toLocaleDateString()}
                    </div>
                  )}
                  {pack.tags?.map((tag) => (
                    <Badge key={tag} variant="secondary">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by part number or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="input-search-products"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {availableCategories.map((filter) => (
                <Button
                  key={filter.value}
                  variant={selectedCategory === filter.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(filter.value)}
                  className={cn(
                    selectedCategory === filter.value && "bg-slate-800 text-white hover:bg-slate-700"
                  )}
                  data-testid={`filter-${filter.value}`}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>

          {(displayedCards.length > 0 || filteredCards.length > 0) ? (
            <div 
              className={cn(
                "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-opacity duration-200",
                isTransitioning && "opacity-0"
              )}
            >
              {(displayedCards.length > 0 ? displayedCards : filteredCards).map((card, index) => (
                <div
                  key={card.id}
                  className={cn(
                    "transition-all duration-300 ease-out",
                    !isTransitioning && "animate-in fade-in-0 slide-in-from-bottom-2"
                  )}
                  style={{
                    animationDelay: !isTransitioning ? `${index * 40}ms` : undefined,
                    animationFillMode: "both"
                  }}
                >
                  <ProductCard
                    id={card.id}
                    title={card.title}
                    description={card.description}
                    category={card.category}
                    image={card.image}
                    specifications={parseSpecs(card.specs)}
                    shortlistButtonText={card.shortlistButtonText}
                    productType={card.productType}
                    technologyType={card.technologyType}
                    onQuickView={() => setQuickViewCard(card)}
                    onAddToEnquiry={() => handleAddToEnquiry(card)}
                    isInEnquiry={isCardInEnquiry(card.id)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
              <h2 className="text-xl font-semibold mb-2">
                {searchQuery || selectedCategory !== "all" 
                  ? "No Matching Products" 
                  : "No Products Yet"}
              </h2>
              <p className="text-muted-foreground">
                {searchQuery || selectedCategory !== "all"
                  ? "Try adjusting your search or filter criteria."
                  : "This collection doesn't have any products yet."}
              </p>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />

      <ProductQuickViewModal
        open={!!quickViewCard}
        onOpenChange={(open) => !open && setQuickViewCard(null)}
        product={quickViewCard ? {
          id: quickViewCard.id,
          title: quickViewCard.title,
          description: quickViewCard.description,
          category: quickViewCard.category,
          image: quickViewCard.image,
          specifications: parseSpecs(quickViewCard.specs),
        } : null}
        onAddToEnquiry={() => {
          if (quickViewCard) {
            handleAddToEnquiry(quickViewCard);
          }
        }}
        isInEnquiry={quickViewCard ? isCardInEnquiry(quickViewCard.id) : false}
      />
    </div>
  );
}
